"use client";

import { Button } from "@/components/ui/button";
import {
  GAME_THEATER_CHROME_SIDE,
  type GameTheaterChromeSide,
  getGameTheaterRailExpandAnchorClass,
  isGameTheaterRailLabelBeforeIcon,
} from "@/lib/constants/game-theater-chrome";
import { cn } from "@/lib/utils";
import type { ButtonHTMLAttributes, ReactNode } from "react";

const RAIL_SLOT_CLASS = "relative h-10 w-10 shrink-0 overflow-visible md:h-12 md:w-12";

const ICON_BOX_CLASS = "flex h-10 w-10 shrink-0 items-center justify-center md:h-12 md:w-12";

function railLabelClass(side: GameTheaterChromeSide): string {
  return cn(
    "inline-block overflow-hidden whitespace-nowrap text-md font-bold",
    "max-w-0 opacity-0 motion-reduce:transition-none",
    "transition-[max-width,opacity,margin] duration-200 ease-out",
    "group-hover/rail:max-w-[9rem] group-hover/rail:opacity-100",
    "group-focus-visible/rail:max-w-[9rem] group-focus-visible/rail:opacity-100",
    side === "right"
      ? "group-hover/rail:mr-2 group-focus-visible/rail:mr-2"
      : "group-hover/rail:ml-2 group-focus-visible/rail:ml-2"
  );
}

export interface TheaterRailIconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "href"> {
  label: string;
  icon: ReactNode;
  slotClassName?: string;
  side?: GameTheaterChromeSide;
  /** When set, render an `<a>` so Classroom (and similar) destinations stay real links. */
  href?: string;
  target?: string;
  rel?: string;
}

/**
 * Side-rail control: icon circle at rest, pill with text growing inward on
 * hover/focus so the chrome width stays icon-sized.
 */
export function TheaterRailIconButton({
  label,
  icon,
  slotClassName,
  side = GAME_THEATER_CHROME_SIDE,
  className,
  type = "button",
  href,
  target,
  rel,
  ...buttonProps
}: TheaterRailIconButtonProps) {
  const labelBeforeIcon = isGameTheaterRailLabelBeforeIcon(side);
  const labelNode = (
    <span data-theater-rail-label="" aria-hidden="true" className={railLabelClass(side)}>
      {label}
    </span>
  );
  const iconNode = (
    <span className={ICON_BOX_CLASS} aria-hidden>
      {icon}
    </span>
  );
  const content = labelBeforeIcon ? (
    <>
      {labelNode}
      {iconNode}
    </>
  ) : (
    <>
      {iconNode}
      {labelNode}
    </>
  );
  const railClassName = cn(
    "group/rail absolute top-0 z-10 h-10 w-10 !gap-0 overflow-hidden !rounded-full bg-brand-purple p-0 text-white hover:z-30 hover:bg-brand-purple-dark focus-visible:z-30 md:h-12 md:w-12",
    side === "right" ? "!justify-end" : "!justify-start",
    getGameTheaterRailExpandAnchorClass(side),
    "[@media(hover:hover)]:hover:!w-max focus-visible:!w-max",
    side === "right"
      ? "[@media(hover:hover)]:hover:!pl-4 focus-visible:!pl-4"
      : "[@media(hover:hover)]:hover:!pr-4 focus-visible:!pr-4",
    // Bottom bar: sit in normal flow. Hover labels are CSS-hidden there.
    "max-md:portrait:!relative max-md:portrait:!inset-auto max-md:portrait:!right-auto max-md:portrait:!left-auto",
    className
  );

  return (
    <div className={cn(RAIL_SLOT_CLASS, slotClassName)}>
      {href ? (
        <Button
          {...buttonProps}
          asChild
          size="icon"
          aria-label={buttonProps["aria-label"] ?? label}
          data-theater-rail-button=""
          data-expand-side={side}
          className={railClassName}
        >
          <a href={href} target={target} rel={rel}>
            {content}
          </a>
        </Button>
      ) : (
        <Button
          {...buttonProps}
          type={type}
          size="icon"
          aria-label={buttonProps["aria-label"] ?? label}
          data-theater-rail-button=""
          data-expand-side={side}
          className={railClassName}
        >
          {content}
        </Button>
      )}
    </div>
  );
}
